import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { CiCalendarDate } from "react-icons/ci";
import Navbardesign from '../Navbar';
import Footer from '../FooterDomain/Footerdata';
import ProjectListingPage from "../Aboutpage/ProjectDetailshome";
import ContactBar from "../Fixedpossion/Fixedposion";
import { Weight } from 'lucide-react';

// 1. Define the CSS styles as JavaScript objects
const styles = {
    blogDetailContainer: {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '2rem',
        fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
    },
    blogHeader: {
        textAlign: 'center',
            borderBottom: '2px solid #ffffffff',
    },
    blogTitle: {
        fontSize: '18px',
        color: '#333',
        marginBottom: '1rem',
        lineHeight: '1.2',
    },
    blogMeta: {
        display: 'flex',
        justifyContent: 'center',
        gap: '2rem',
        color: '#666',
        fontSize: '0.9rem',
    },
    blogCategory: {
        backgroundColor: '#e9f5ff',
        color: '#0066cc',
        padding: '0.25rem 0.75rem',
        borderRadius: '20px',
        fontWeight: '600',
    },
    blogDate: {
        fontWeight: '700',
        color: '#e25d27',
        display: 'flex',
        fontSize: '1rem',
        alignItems: 'center',
        gap: '0.5rem',
    },
    // *** Updated for Centering/Responsiveness ***
    blogImageContainer: {
        margin: '2rem auto', // Centered container
        maxWidth: '700px', // Optional: Set a max width for the container to keep the image centered and not too wide
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
    },
    blogImage: {
        width: '100%', // Make the image fill the container (up to maxWidth of container)
        height: 'auto',
        display: 'block',
        transition: 'transform 0.3s ease-in-out', // Added transition here
    },
    // ********************************************
    blogContent: {
        marginTop: '2rem',
        lineHeight: '1.8',
        color: '#444',
        // Important: When rendering rich text (HTML), you might want to scope its styles
        '& p': { margin: '1rem 0' },
        '& h2, & h3': { color: '#333', marginTop: '2rem', marginBottom: '1rem' },
    },
    blogStats: {
        backgroundColor: '#f8f9fa',
        padding: '1.5rem',
        borderRadius: '8px',
        marginBottom: '2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    blogStatsP: {
        margin: 0,
        color: '#555',
    },
    status: {
        padding: '0.25rem 0.75rem',
        borderRadius: '4px',
        fontWeight: '600',
    },
    statusActive: {
        backgroundColor: '#d4edda',
        color: '#155724',
    },
    statusInactive: {
        backgroundColor: '#f8d7da',
        color: '#721c24',
    },
    loading: {
        textAlign: 'center',
        padding: '3rem',
        fontSize: '1.2rem',
        color: '#666',
    },
    error: {
        textAlign: 'center',
        padding: '3rem',
        color: '#dc3545',
        fontSize: '1.2rem',
    },
    notFound: {
        textAlign: 'center',
        padding: '3rem',
        color: '#666',
        fontSize: '1.2rem',
    }
};

// 2. Define the structural/complex CSS (like :hover and @media) as a string
// Added CSS to scope styles for the content body.
const embeddedCSS = `
    .blog-image:hover {
        transform: scale(1.05); /* Slightly increased scale for better effect */
    }
    .status.active {
        background-color: #d4edda;
        color: #155724;
    }
    .status.inactive {
        background-color: #f8d7da;
        color: #721c24;
    }
    .blog-content p {
        margin: 1em 0;
    }
    .blog-content h2, .blog-content h3 {
        color: #333; 
        margin-top: 2rem; 
        margin-bottom: 1rem;
    }
    @media (max-width: 768px) {
        .blog-detail-container {
            padding: 1rem;
        }
        .blog-title {
            font-size: 1.8rem;
        }
        .blog-meta {
            flex-direction: column;
            gap: 0.5rem;
            align-items: center;
        }
        .blog-stats {
            flex-direction: column;
            gap: 1rem;
            text-align: center;
        }
        /* Ensure image container is fully responsive on smaller screens */
        .blog-image-container {
            margin: 1rem 0;
            max-width: 100%;
        }
        .blog-image {
            width: 100%;
        }
    }
`;


const BlogDetailpageContaint = () => {
    const { id } = useParams();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchBlog = async () => {
            try {
                setLoading(true);
                // It's assumed the API returns a blog object structure like:
                // { title, content, category, status, image, created_at, views }
                // I've used a mock URL for this example, replace it with your actual backend call if it's different from the provided:
                const API_URL = `https://mithahomesfrontend.pemixcels.com/api/blogs/get/${id}`; 
                const response = await axios.get(API_URL);
                
                if (response.data.blog) {
                    setBlog(response.data.blog);
                } else {
                    // Check if the response indicates no blog found, even with a 200 status
                    setError('Blog not found');
                }
            } catch (err) {
                // Catch network or 4xx/5xx errors
                setError('Failed to fetch blog details. Please check the network or API endpoint.');
                console.error('Error fetching blog:', err);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchBlog();
        }
    }, [id]);

    if (loading) {
        return <div style={styles.loading}>Loading blog...</div>;
    }

    if (error) {
        return <div style={styles.error}>{error}</div>;
    }

    if (!blog) {
        return <div style={styles.notFound}>Blog data is unavailable.</div>;
    }
    
    // Determine status style
    const statusStyle = blog.status === 'active' ? styles.statusActive : styles.statusInactive;
    const combinedStatusStyle = { ...styles.status, ...statusStyle };
    
    // Fallback for image URL if it's missing or fails to load
    const imageUrl = blog.image 
        ? `https://mithahomesfrontend.pemixcels.com${blog.image}` 
        : '/default-blog-image.jpg';

    return (
        // 3. Embed the complex CSS via a <style> tag
        <>
            <style dangerouslySetInnerHTML={{ __html: embeddedCSS }} />
            
            <Navbardesign/>
            <br/>
            <br/>
            <br/>
            <div style={styles.blogDetailContainer} className="blog-detail-container">
                <div style={styles.blogHeader} className="blog-header">
                    <div style={styles.blogMeta} className="blog-meta">
                       
                        <span style={styles.blogDate} className="blog-date">
                            <CiCalendarDate /> 
                            Posted on &nbsp;&nbsp;
                            {new Date(blog.created_at).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                            })}
                        </span>
                    </div>
                    <h1 style={styles.blogTitle} className="blog-title">{blog.title}</h1>
                  
                </div>

                <div style={styles.blogImageContainer} className="blog-image-container">
                    <img 
                        src={imageUrl}
                        alt={blog.title}
                        style={styles.blogImage}
                        className="blog-image" // Keep class for :hover/media queries defined in <style>
                        onError={(e) => {
                            // Only set default image if the URL is not the default already
                            if (e.target.src !== '/default-blog-image.jpg') {
                                e.target.onerror = null; // prevents looping if default image also fails
                                e.target.src = '/default-blog-image.jpg';
                            }
                        }}
                    />
                </div>
                
                {/* Blog Statistics/Status Section */}
             
                {/* Blog Content Section */}
                <div style={styles.blogContent} className="blog-content">
                    {/* Assuming blog.content contains HTML/rich text that needs to be rendered */}
                    <div 
                        dangerouslySetInnerHTML={{ __html: blog.content }} 
                    />
                </div>

             
            </div>
      <Footer/>
        <br/>
        <br/>
        <br/>
      <ContactBar/> 
        </>
    );
};

export default BlogDetailpageContaint;